/**
 * Pluggable providers for the two primitives that are not portable to every
 * JS runtime: Argon2id and Blake2b, both served by `hash-wasm` (WebAssembly)
 * by default. WASM is unavailable on React Native/Hermes, so RN hosts inject
 * replacements (native Argon2id, pure-JS Blake2b) at app startup — BEFORE any
 * derive/seal call — via {@link setE2eCryptoProvider}.
 *
 * Injected implementations MUST reproduce `vectors/wire-vectors.json` exactly;
 * a drifted Argon2id silently breaks verifier login and unlock_blob decryption.
 */

export interface Argon2idParams {
  /** Memory cost (KiB). */
  memoryKib: number;
  /** Time cost (iterations). */
  iterations: number;
  parallelism: number;
  /** Raw output length (bytes). */
  outputLen: number;
}

export interface E2eCryptoProvider {
  /** Argon2id(password, salt) → `params.outputLen` raw bytes. */
  argon2id?: (
    password: string,
    salt: Uint8Array,
    params: Argon2idParams,
  ) => Promise<Uint8Array>;
  /** Unkeyed Blake2b(input) → `outputLen` raw bytes. */
  blake2b?: (input: Uint8Array, outputLen: number) => Promise<Uint8Array>;
}

let provider: E2eCryptoProvider = {};

/** Merge-install runtime overrides (RN). Omitted fields keep the wasm default. */
export function setE2eCryptoProvider(p: E2eCryptoProvider): void {
  provider = { ...provider, ...p };
}

function hexToBytes(hex: string, len: number): Uint8Array {
  const out = new Uint8Array(len);
  for (let i = 0; i < len; i++) {
    out[i] = parseInt(hex.slice(i * 2, i * 2 + 2), 16);
  }
  return out;
}

export async function argon2id(
  password: string,
  salt: Uint8Array,
  params: Argon2idParams,
): Promise<Uint8Array> {
  if (provider.argon2id) return provider.argon2id(password, salt, params);
  const { argon2id: wasmArgon2id } = await import('hash-wasm');
  const hex = await wasmArgon2id({
    password,
    salt,
    parallelism: params.parallelism,
    iterations: params.iterations,
    memorySize: params.memoryKib,
    hashLength: params.outputLen,
    outputType: 'hex',
  });
  return hexToBytes(hex, params.outputLen);
}

export async function blake2b(
  input: Uint8Array,
  outputLen: number,
): Promise<Uint8Array> {
  if (provider.blake2b) return provider.blake2b(input, outputLen);
  const { blake2b: wasmBlake2b } = await import('hash-wasm');
  const hex = await wasmBlake2b(input, outputLen * 8);
  return hexToBytes(hex, outputLen);
}
